/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { V3MemberSearchComponent } from '@app/app/shared/components-v3/form-content-elements/form-booking/member-search/v3-member-search.component';
import { isEmailAddress, skippedReasonKey, summarise } from '@app/app/shared/helpers/admin-enrolment.helper';
import { UserInfo } from '@app/app/shared/models/analytics/analytics.model';
import { AdminEnrolment, AdminEnrolmentPerson, AdminEnrolmentRequest } from '@app/app/shared/models/playlist/admin-enrolment.model';
import { ADMIN_ENROLMENTS_DATA_SERVICE } from '@app/app/shared/services/admin-enrolments/admin-enrolments-data.service';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';
import { PtlV3DialogShellComponent } from '@app/app/shared/styleguide/components/dialog-shell/ptl-v3-dialog-shell.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { LucideInfo, LucideTriangleAlert, LucideX } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { EMPTY, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs';

/** @property published - people are only emailed once the content is published */
export interface EnrolDialogData {
  playlistUid: string;
  contentTitle: string;
  published: boolean;
}

const PREVIEW_DEBOUNCE_MS = 400;

/** Enrols one person: someone found in the workspace, or a new person by address, who gets an account. */
@Component({
  selector: 'ptl-v3-enrol-person-dialog',
  templateUrl: './enrol-person-dialog.component.html',
  styleUrls: ['../admin-enrolment-dialog.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, StyleguideModule, PtlV3DialogShellComponent, V3MemberSearchComponent],
})
export class EnrolPersonDialogComponent {
  protected readonly data = inject<EnrolDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<EnrolPersonDialogComponent, AdminEnrolment>);
  private readonly service = inject(ADMIN_ENROLMENTS_DATA_SERVICE);
  private readonly toastService = inject<ToastService>(TOAST_NOTIFICATION_SERVICE);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly LucideInfo = LucideInfo;
  protected readonly LucideTriangleAlert = LucideTriangleAlert;
  protected readonly LucideX = LucideX;

  readonly selected = signal<UserInfo | null>(null);
  readonly email = signal('');
  readonly firstName = signal('');
  readonly lastName = signal('');
  readonly externalId = signal('');
  readonly notify = signal(true);

  readonly preview = signal<AdminEnrolment | null>(null);
  readonly enrolling = signal(false);
  readonly failure = signal<string | null>(null);

  readonly person = computed<AdminEnrolmentPerson | null>(() => {
    const selected = this.selected();
    if (selected) {
      return { email: selected.email };
    }
    const email = this.email().trim();
    if (!isEmailAddress(email)) {
      return null;
    }
    return {
      email,
      firstName: this.firstName().trim() || undefined,
      lastName: this.lastName().trim() || undefined,
      externalId: this.externalId().trim() || undefined,
    };
  });

  readonly emailInvalid = computed(() => !this.selected() && this.email().trim() !== '' && !isEmailAddress(this.email()));

  readonly result = computed(() => this.preview()?.people[0] ?? null);

  /** No account uses the address: the API wants names for the one it creates. */
  readonly needsAccount = computed(() => {
    const result = this.result();
    return !this.selected() && !!result && (result.newAccount || result.problem === 'MISSING_NAME');
  });

  readonly blockingReasonKey = computed(() => {
    const result = this.result();
    if (!result || result.outcome === 'ENROLLED' || result.problem === 'MISSING_NAME') {
      return null;
    }
    return skippedReasonKey(result);
  });

  readonly seatsOver = computed(() => {
    const preview = this.preview();
    return preview && this.result()?.outcome === 'ENROLLED' ? summarise(preview).seatsOver : null;
  });

  readonly outsideAudience = computed(() => this.result()?.outcome === 'ENROLLED' && !!this.result()?.outsideAudience);

  readonly displayName = computed(() => {
    const selected = this.selected();
    if (selected) {
      return `${selected.firstName ?? ''} ${selected.lastName ?? ''}`.trim() || selected.email;
    }
    return this.firstName().trim() || this.email().trim();
  });

  readonly canEnrol = computed(() => {
    if (this.enrolling() || !this.person() || this.blockingReasonKey()) {
      return false;
    }
    return !this.needsAccount() || (!!this.firstName().trim() && !!this.lastName().trim());
  });

  constructor() {
    toObservable(this.person)
      .pipe(
        debounceTime(PREVIEW_DEBOUNCE_MS),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        tap(() => this.preview.set(null)),
        switchMap((person) => (person ? this.service.previewPerson(this.data.playlistUid, person) : EMPTY)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ isSuccess, value }) => this.preview.set(isSuccess ? value : null));
  }

  onSelected(user: UserInfo): void {
    this.failure.set(null);
    this.selected.set(user);
  }

  clearSelected(): void {
    this.selected.set(null);
  }

  close(): void {
    this.dialogRef.close();
  }

  enrol(): void {
    const person = this.person();
    if (!person || !this.canEnrol()) {
      return;
    }
    const selected = this.selected();
    const request: AdminEnrolmentRequest = {
      userUids: selected ? [selected.userId] : [],
      people: selected ? [] : [person],
      notify: this.notify(),
    };
    this.enrolling.set(true);
    this.failure.set(null);
    this.service
      .enrol(this.data.playlistUid, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ isSuccess, value, error }) => {
        this.enrolling.set(false);
        const result = value?.people[0];
        if (!isSuccess || !result) {
          this.failure.set(error || this.transloco.translate('translations.adminEnrolment.error.enrol'));
          return;
        }
        if (result.outcome !== 'ENROLLED') {
          this.failure.set(this.transloco.translate(skippedReasonKey(result) ?? 'translations.adminEnrolment.error.enrol'));
          return;
        }
        this.toastService.showSuccess(
          this.transloco.translate('translations.adminEnrolment.person.enrolled', { name: this.displayName() }),
        );
        this.dialogRef.close(value);
      });
  }
}
